<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1 v-if="isNew">{{ $t('add_product.new_product') }}</h1>
      <h1 v-else>{{ $t('add_product.edit_product') }}</h1>
    </div>
    <div class="details-component__content content">
      <form class="form">
        <fieldset class="form__fieldset">

          <div class="form__field form__field_radiobuttons" :class="{'form__field_error' : errors.has('productType')}" v-show="isNew">
            <label for="productType" class="form__label">{{ $tc('add_permit.product', 'product.total') }}</label>
            <div class="radiobuttons">
              <template v-for="productType in productTypes">
                <div :key="productType" class="radiobuttons__element" ref="productType">
                  <label>
                    <input type="radio" name="productType" v-model="type"
                      :id="productType"
                      :value="productType"
                      role="select productType"
                      v-validate="'required'"/>
                    <span class="radiobuttons__control" ></span>
                    <span :for="productType" class="radiobuttons__description">{{ $t('product.' + productType) }}</span>
                  </label>
                </div>
              </template>
            </div>
            <tooltip v-show="errors.has('productType')" class="form__validation">{{ $t('errors.product_type_required') }}</tooltip>
          </div>
          <div class="form__field" :class="{'form__field_error' : errors.has('name')}">
            <label for="name" class="form__label">{{ $t('common.name') }}</label>
            <input type="text"
            class="form__input"
              name="name"
              id="name"
              value=""
              v-model="form.name"
              v-validate
              data-vv-rules="required|min:3|max:255"
              data-vv-as="name"
              data-vv-delay="1000" />
            <tooltip v-show="errors.has('name:required')" class="form__validation">{{ $t('errors.field_required') }}</tooltip>
          </div>
          <h5 :class="{'error' : errors.has('zone')}"><span v-if="zones.length > 0">{{ $tc('common.zones', zones.length) }}</span></h5>
          <div class="form__field form__field_checkboxlist">
            <template v-for="zone in zones">
              <label class="" :key="zone.id">
                  <input type="checkbox"  name="zone" v-model="checkedZones" v-validate="'required'" :id="zone.id" :value="zone.id" class="form__fieldset__checkbox" >
                  <span v-model="form.checkedZones" class="form__fieldset__custom-checkbox"></span>
                  <label :for="zone.id" class="form__label">{{`${zone.code} (${zone.address})`}}</label>
              </label>
            </template>
            <tooltip v-show="errors.has('zone')" class="form__validation">{{ $t('errors.zone_required') }}</tooltip>
          </div>

          <div class="form__field" :class="{'form__field_error' : errors.has('permit_count')}" v-if="isPermitPack()">
            <label for="permit_count" class="form__label">{{ $t('add_product.parking_spots') }}</label>
            <input type="text"
              class="form__input"
              name="permit_count"
              id="permit_count"
              value=""
              v-model="form.total"
              v-validate
              data-vv-rules="required|numeric|min:1|cannotBeLower"
              data-vv-as="$t('add_product.parking_spots')"
              data-vv-delay="1000" />
            <tooltip v-show="errors.has('permit_count:numeric')" class="form__validation">{{ $t('errors.field_numeric') }}</tooltip>
            <tooltip v-show="errors.has('permit_count:required')" class="form__validation">{{ $t('errors.field_required') }} {{ form.total }}</tooltip>
            <tooltip v-show="errors.has('permit_count:cannotBeLower')" class="form__validation">{{ $t('errors.permit_count_too_low') }} {{ form.issued }}</tooltip>
          </div>

          <div class="form__field" :class="{'form__field_error' : errors.has('commission_value') || errors.has('commission_type')}" v-if="isGuestParking() || isEasyParking()">
            <label for="commission" class="form__label">{{ $t('products.commission') }}</label>
            <div>
              <input type="text"
              class="form__input"
                name="commission_value"
                id="commission_value"
                value=""
                v-model="form.commission_value"
                v-validate
                data-vv-rules="decimal:2"
                data-vv-as="$t('products.commission')"
                data-vv-delay="1000" />
             </div>
             <el-select name="commission_type"
              v-model="commissionType"
              placeholder=""
              v-validate="`${form.commission_value ? 'required' : ''}`">
              <el-option value="" label=""></el-option>
              <el-option value="%" label="%"></el-option>
              <el-option value="€" :label="currency_short"></el-option>
            </el-select>
            <tooltip v-show="errors.has('commission_value')" class="form__validation">{{ $t('errors.field_numeric') }}</tooltip>
            <tooltip v-show="errors.has('commission_type')" class="form__validation">{{ $t('errors.commission_type_required') }}</tooltip>
          </div>

          <div class="form__field" :class="{'form__field_error' : errors.has('perDay:numeric')}" v-if="isGuestParking() || isEasyParking()">
            <label for="perDay" class="form__label">{{ $t('products.per_day') }}</label>
            <input type="text"
            class="form__input"
              name="perDay"
              id="perDay"
              value=""
              v-model="form.perDay"
              v-validate
              data-vv-rules="numeric|min:1"
              data-vv-as="perDay"
              data-vv-delay="1000" />
            <tooltip v-show="errors.has('perDay:numeric')" class="form__validation">{{ $t('errors.field_numeric') }}</tooltip>
          </div>

          <div class="form__field" :class="{'form__field_error' : errors.has('perDay:numeric')}" v-if="isGuestParking() || isEasyParking()">
            <label for="concurrentParkings" class="form__label">{{ $t('products.concurrent_parkings') }}</label>
            <input type="text"
            class="form__input"
              name="concurrentParkings"
              id="concurrentParkings"
              value=""
              v-model="form.concurrentParkings"
              v-validate
              data-vv-rules="numeric|min:1"
              data-vv-as="concurrentParkings"
              data-vv-delay="1000" />
            <tooltip v-show="errors.has('concurrentParkings:numeric')" class="form__validation">{{ $t('errors.field_numeric') }}</tooltip>
          </div>

          <div class="form__field" v-if="isGuestParking() || isEasyParking()">
            <label for="frequency" class="form__label">{{ $t('products.frequency') }}</label>
            <el-select name="frequency"
              v-model="frequency"
              placeholder="">
              <el-option
                v-for="step of steps"
                :label="step.value"
                :value="step.key"
                :key="step.key">
              </el-option>
            </el-select>
          </div>

          <div class="form__field"  v-if="isPermitPack()">
            <label class="form__label form__label__long">
              <input type="checkbox" id="can_exceed_capacity" name="can_exceed_capacity" class="form__fieldset__checkbox" v-model="form.canExceedCapacity">
              <span v-model="form.canExceedCapacity" class="form__fieldset__custom-checkbox"></span>
              <span class="custom-checkbox-label">{{ $t('add_product.enable_virtual_parking_spots') }}</span>
            </label>
          </div>

        </fieldset>

        <div class="form__buttons buttonset">
          <router-link v-if="isNew" :to="{ name: 'products' }" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <router-link v-else :to="{ name: 'product', params: { id: $route.params.id }}" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <button type="submit" class="buttonset__button btn btn--main" v-on:click.prevent.stop="onSubmit()">
          {{ $t('common.save') }}</button>
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import * as mTypes from 'store/mutation-types';
import Tooltip from 'common/Tooltip';
import 'styles/blocks/details.scss';

export default {
  components: {
    Tooltip,
  },
  watch: {
    $route: 'fetchAllData',
  },
  computed: {
    isNew() {
      return !this.$route.params.id || this.$route.params.id === 'new';
    },
    ...mapGetters({
      zones: 'products/zones/zones',
      product: 'products/product/product',
      productsLimit: 'landlord/products/limit',
      productsKeywordFilter: 'landlord/products/keywordFilter',
      productError: 'products/product/error',
      user: 'user/user/user',
    }),
  },
  mounted() {
    this.fetchAllData();
  },
  created() {
    this.$validator.remove('cannotBeLower');
    this.$validator.extend('cannotBeLower', {
      getMessage: () => 'Number cannot be lower then assigned permits',
      validate: (value) => {
        if (!this.form.canExceedCapacity && this.product.issued) {
          return value >= this.product.issued;
        }
        return true;
      },
    });
  },
  data() {
    return {
      currency: process.env.CURRENCY,
      currency_short: process.env.CURRENCY_SHORT,
      form: {},
      frequency: 0,
      commissionType: null,
      type: 'permitPack',
      checkedZones: [],
      steps: [
        { key: -1440, value: `-24 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -720, value: `-12 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -480, value: `-8 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -360, value: `-6 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -300, value: `-5 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -240, value: `-4 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -180, value: `-3 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -120, value: `-2 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -90, value: `-1 ${this.$t('pricingrules.hour')} 30 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: -60, value: `-1 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -45, value: `-45 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: -30, value: `-30 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: -15, value: `-15 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: -10, value: `-10 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: -5, value: `-5 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 0, value: '' },
        { key: 5, value: `5 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 10, value: `10 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 15, value: `15 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 30, value: `30 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 45, value: `45 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 60, value: `1 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 90, value: `1 ${this.$t('pricingrules.hour')} 30 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 120, value: `2 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 180, value: `3 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 240, value: `4 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 300, value: `5 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 360, value: `6 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 480, value: `8 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 720, value: `12 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 1440, value: `24 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
      ],
      productTypes: ['permitPack', 'guestParking', 'easyParking'],
    };
  },
  methods: {
    fetchAllData() {
      this.fetchZones();
      if (this.$route.params.id) {
        this.fetchProduct();
      } else {
        this.$store.commit(`products/product/${mTypes.CLEAR_STATE}`);
        this.checkedZones = [];
        this.form = {};
      }
    },
    fetchZones() {
      this.$store.dispatch('products/zones/fetch', { user: this.user });
    },
    fetchProduct() {
      this.$store.dispatch('products/product/fetch', { id: this.$route.params.id, user: this.user }).then(() => {
        this.form = this.product;
        this.type = this.product.type;
        if (this.product.commission) {
          this.form.commission_value = this.product.commission.value;
          this.commissionType = this.product.commission.type;
        }
        if (this.product.frequency) {
          this.frequency = this.product.frequency;
        }
        this.product.zones.forEach((el) => {
          this.checkedZones.push(el.id);
        });
      });
    },
    onSubmit() {
      this.$store.commit(`products/product/${mTypes.DATA_ERROR}`, null);
      this.$validator.validateAll()
        .then((success) => {
          if (!success) {
            return;
          }
          this.form.type = this.type;
          this.form.zones = [];
          this.checkedZones.forEach((el) => {
            this.form.zones.push({ id: el });
          });
          if (!this.form.canExceedCapacity) {
            this.form.canExceedCapacity = false;
          }
          if (!this.form.total) {
            this.form.total = 0;
          }
          if (!this.form.perDay) {
            this.form.perDay = null;
          }
          if (this.frequency) {
            this.form.frequency = this.frequency;
          }
        if (this.frequency == 0) { // eslint-disable-line
            this.form.frequency = null;
          }
          if (this.form.commission_value && this.form.commission_value !== '') {
            this.form.commission = {};
            this.form.commission.value = this.form.commission_value;
            this.form.commission.type = this.commissionType;
          } else {
            this.form.commission = null;
          }
          this.$store.dispatch('products/product/saveProduct', { product: this.form, user: this.user }).then(() => {
            if (!this.productError) {
              this.form = {};
              this.$store.dispatch('landlord/products/fetch', { user: this.user, limit: this.productsLimit, keyword: this.productsKeywordFilter });
              this.$router.push({ name: 'product', params: { id: this.product.id } });
            }
          })
            .catch(() => {
              this.$store.commit(`products/product/${mTypes.DATA_ERROR}`, true);
            });
        })
        .catch(() => {
        });
    },
    isPermitPack() {
      return this.type === 'permitPack';
    },
    isGuestParking() {
      return this.type === 'guestParking';
    },
    isEasyParking() {
      return this.type === 'easyParking';
    },
  },
};
</script>
<style lang="scss" scoped>
  .virtual {
    width: 2600px;
  }
  .form__fieldset {
    min-width: 0;

    .form__field {

      > label {
        padding-left: 50px;

        > .form__fieldset__custom-checkbox {
          margin-left: -50px;
        }

      }

    }

  }


</style>



// WEBPACK FOOTER //
// src/components/products/AddProductView.vue